// ============================================================================
// opfs.ts — Origin Private File System: хранилище КРУПНЫХ блобов (медиа/ассеты),
// где IndexedDB неэффективен (память, сериализация, лимиты). Мелкие блобы —
// IndexedDB (быстро, кэш SW); крупные (≥ порога fileSync) — OPFS.
//
// Возможность определяется детерминированно (feature-detect): нет OPFS
// (старый браузер / insecure context) → методы возвращают null/false, и
// потребитель использует IndexedDB. Это НЕ фоллбэк бизнес-логики, а выбор
// доступного хранилища (как SupportsSchema у DBAdapter).
// ============================================================================

const OPFS_DIR = 'hydra-blobs';

type DirHandle = FileSystemDirectoryHandle;

let dirPromise: Promise<DirHandle | null> | null = null;

/** Каталог блобов (создаётся при первом обращении); null — OPFS недоступен. */
async function opfsDir(): Promise<DirHandle | null> {
  if (dirPromise) return dirPromise;
  dirPromise = (async (): Promise<DirHandle | null> => {
    try {
      if (typeof navigator === 'undefined') return null;
      const storage = navigator.storage as (StorageManager & { getDirectory?: () => Promise<DirHandle> }) | undefined;
      if (!storage || typeof storage.getDirectory !== 'function') return null;
      const root = await storage.getDirectory();
      return await root.getDirectoryHandle(OPFS_DIR, { create: true });
    } catch {
      return null;
    }
  })();
  return dirPromise;
}

export async function opfsAvailable(): Promise<boolean> {
  return (await opfsDir()) !== null;
}

/** Имя файла в OPFS — безопасное (id файлов — UUID/hex, но экранируем). */
function entryName(id: string): string {
  return encodeURIComponent(id);
}

export async function opfsGet(id: string): Promise<Blob | null> {
  const dir = await opfsDir();
  if (!dir) return null;
  try {
    const fh = await dir.getFileHandle(entryName(id));
    const file = await fh.getFile();
    const buf = await file.arrayBuffer();
    return new Blob([buf], { type: file.type || 'application/octet-stream' });
  } catch {
    return null;
  }
}

export async function opfsSet(id: string, blob: Blob): Promise<boolean> {
  const dir = await opfsDir();
  if (!dir) return false;
  try {
    const fh = await dir.getFileHandle(entryName(id), { create: true });
    const writable = await fh.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

export async function opfsDelete(id: string): Promise<void> {
  const dir = await opfsDir();
  if (!dir) return;
  try {
    await dir.removeEntry(entryName(id));
  } catch {
    // нет файла — не ошибка
  }
}

/** Оценка доступности OPFS (диагностика). */
export async function opfsUsage(): Promise<{ available: boolean }> {
  return { available: (await opfsDir()) !== null };
}

/** Полная очистка OPFS-каталога блобов (имитация удаления приложения). */
export async function opfsClear(): Promise<void> {
  dirPromise = null;
  try {
    if (typeof navigator === 'undefined') return;
    const storage = navigator.storage as (StorageManager & { getDirectory?: () => Promise<DirHandle> }) | undefined;
    if (!storage || typeof storage.getDirectory !== 'function') return;
    const root = await storage.getDirectory();
    await root.removeEntry(OPFS_DIR, { recursive: true });
  } catch {
    // нет каталога — не ошибка
  }
}
